/**
 * Lighting maps for the floor shader, computed once per photo (and again when
 * the mask changes) on a downsampled copy of the photo:
 * - Lblur: heavily blurred linear luminance (radius ~1.5% of width). Keeps broad
 *   light and shadow, removes stains, cracks, oil spots and patterns such as
 *   tiles or paint (retinex). Blurred with the mask as weights so walls do not
 *   bleed into the floor edges.
 * - Lsmall: lightly blurred lighting (after retinex), for gloss reflections.
 * - Lref: median Lblur over the floor area.
 */

export const LIGHT_LONG_EDGE = 512;

export interface LightingMaps {
  width: number;
  height: number;
  /** Interleaved RG: R = Lblur, G = Lsmall (linear luminance 0..1). */
  data: Float32Array;
  lref: number;
}

const SRGB_TO_LINEAR = new Float32Array(256);
for (let i = 0; i < 256; i++) {
  const c = i / 255;
  SRGB_TO_LINEAR[i] = c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Separable box blur with clamped edges, in place. */
function boxBlur(src: Float32Array, w: number, h: number, r: number) {
  if (r < 1) return;
  const tmp = new Float32Array(src.length);
  const norm = 1 / (2 * r + 1);
  for (let y = 0; y < h; y++) {
    const o = y * w;
    let acc = 0;
    for (let k = -r; k <= r; k++) acc += src[o + Math.min(w - 1, Math.max(0, k))];
    for (let x = 0; x < w; x++) {
      tmp[o + x] = acc * norm;
      acc += src[o + Math.min(w - 1, x + r + 1)] - src[o + Math.max(0, x - r)];
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let k = -r; k <= r; k++) acc += tmp[Math.min(h - 1, Math.max(0, k)) * w + x];
    for (let y = 0; y < h; y++) {
      src[y * w + x] = acc * norm;
      acc += tmp[Math.min(h - 1, y + r + 1) * w + x] - tmp[Math.max(0, y - r) * w + x];
    }
  }
}

/** Three box passes approximate a Gaussian. */
function blur(src: Float32Array, w: number, h: number, radius: number) {
  const r = Math.max(1, Math.round(radius / Math.sqrt(3)));
  for (let i = 0; i < 3; i++) boxBlur(src, w, h, r);
}

/**
 * 1D running max over a centered window of 2r+1 (van Herk / Gil-Werman, O(n)).
 * Reads `n` values from `src` at `offset + i * stride` and writes them back the same way.
 */
function runningMax(src: Float32Array, offset: number, stride: number, n: number, r: number, g: Float32Array, hb: Float32Array) {
  const k = 2 * r + 1;
  const m = n + 2 * r;
  const at = (i: number) => (i < r || i >= n + r ? -Infinity : src[offset + (i - r) * stride]);
  for (let i = 0; i < m; i++) {
    const v = at(i);
    g[i] = i % k === 0 ? v : g[i - 1] > v ? g[i - 1] : v;
  }
  for (let i = m - 1; i >= 0; i--) {
    const v = at(i);
    hb[i] = i === m - 1 || (i + 1) % k === 0 ? v : hb[i + 1] > v ? hb[i + 1] : v;
  }
  for (let x = 0; x < n; x++) {
    const a = hb[x];
    const b = g[x + 2 * r];
    src[offset + x * stride] = a > b ? a : b;
  }
}

/** Separable 2D max filter in place. */
function maxFilter(a: Float32Array, w: number, h: number, r: number) {
  const len = Math.max(w, h) + 2 * r;
  const g = new Float32Array(len);
  const hb = new Float32Array(len);
  for (let y = 0; y < h; y++) runningMax(a, y * w, 1, w, r, g, hb);
  for (let x = 0; x < w; x++) runningMax(a, x, w, h, r, g, hb);
}

/**
 * Max (dilate) or min (erode) filter over floor pixels only; non-floor
 * pixels never contribute, so walls cannot leak into the floor. Erosion is
 * a max filter on negated values.
 */
function morph(src: Float32Array, floor: Uint8Array, w: number, h: number, r: number, dilate: boolean): Float32Array {
  const s = dilate ? 1 : -1;
  const a = new Float32Array(src.length);
  for (let i = 0; i < a.length; i++) a[i] = floor[i] ? s * src[i] : -Infinity;
  maxFilter(a, w, h, r);
  for (let i = 0; i < a.length; i++) a[i] = floor[i] && Number.isFinite(a[i]) ? s * a[i] : src[i];
  return a;
}

/**
 * Grayscale opening (erode, then dilate) removes bright features narrower than
 * 2r, such as painted lines and small reflections; closing (dilate, then
 * erode) then fills dark ones, such as oil spots and cracks. Broad light and
 * shadows keep their shape. The new coating covers all of these marks.
 */
export function removeStains(lum: Float32Array, mask: Float32Array, w: number, h: number, r: number): Float32Array {
  const floor = new Uint8Array(lum.length);
  let count = 0;
  for (let i = 0; i < floor.length; i++) {
    if (mask[i] > 0.5) {
      floor[i] = 1;
      count++;
    }
  }
  if (!count) floor.fill(1);
  const opened = morph(morph(lum, floor, w, h, r, false), floor, w, h, r, true);
  return morph(morph(opened, floor, w, h, r, true), floor, w, h, r, false);
}

interface Level {
  w: number;
  h: number;
  floor: Uint8Array;
  div: Float32Array;
  u: Float32Array;
}

/** Masked Gauss-Seidel/SOR on sum over floor neighbors (u_n - u_i) = div_i. */
function relax({ w, h, floor, div, u }: Level, iterations: number) {
  for (let it = 0; it < iterations; it++) {
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (!floor[i]) continue;
        let sum = 0;
        let count = 0;
        if (x > 0 && floor[i - 1]) (sum += u[i - 1]), count++;
        if (x < w - 1 && floor[i + 1]) (sum += u[i + 1]), count++;
        if (y > 0 && floor[i - w]) (sum += u[i - w]), count++;
        if (y < h - 1 && floor[i + w]) (sum += u[i + w]), count++;
        if (!count) continue;
        u[i] += 1.8 * ((sum - div[i]) / count - u[i]);
      }
    }
  }
}

/**
 * Retinex: separates the old floor's own pattern from the lighting. Paint,
 * tiles, stains and texture change brightness abruptly (large log-luminance
 * steps between neighbors); lighting changes gradually. Steps above
 * `threshold` are dropped and the rest is re-integrated (Poisson, solved
 * coarse to fine), leaving the illumination on the floor. Returns linear
 * luminance; non-floor pixels are passed through.
 */
export function retinex(lum: Float32Array, floor: Uint8Array, w: number, h: number, threshold = 0.2): Float32Array {
  const n = w * h;
  const log = new Float32Array(n);
  for (let i = 0; i < n; i++) log[i] = Math.log(Math.max(lum[i], 1e-4));

  // Divergence of the kept gradients, over floor neighbors only.
  const div = new Float32Array(n);
  const step = (a: number, b: number) => {
    const d = log[b] - log[a];
    return Math.abs(d) < threshold ? d : 0;
  };
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (!floor[i]) continue;
      let d = 0;
      if (x > 0 && floor[i - 1]) d += step(i, i - 1);
      if (x < w - 1 && floor[i + 1]) d += step(i, i + 1);
      if (y > 0 && floor[i - w]) d += step(i, i - w);
      if (y < h - 1 && floor[i + w]) d += step(i, i + w);
      div[i] = d;
    }
  }

  // Pyramid: coarse cells are floor if any child is; flux sums restrict exactly.
  const levels: Level[] = [{ w, h, floor, div, u: new Float32Array(n) }];
  while (levels[levels.length - 1].w > 24 && levels[levels.length - 1].h > 24) {
    const f = levels[levels.length - 1];
    const cw = Math.ceil(f.w / 2);
    const ch = Math.ceil(f.h / 2);
    const cf = new Uint8Array(cw * ch);
    const cd = new Float32Array(cw * ch);
    for (let y = 0; y < f.h; y++) {
      for (let x = 0; x < f.w; x++) {
        const i = y * f.w + x;
        if (!f.floor[i]) continue;
        const c = (y >> 1) * cw + (x >> 1);
        cf[c] = 1;
        cd[c] += f.div[i];
      }
    }
    levels.push({ w: cw, h: ch, floor: cf, div: cd, u: new Float32Array(cw * ch) });
  }

  // Solve coarse to fine, using each solution as the next level's start.
  relax(levels[levels.length - 1], 300);
  for (let l = levels.length - 2; l >= 0; l--) {
    const f = levels[l];
    const c = levels[l + 1];
    for (let y = 0; y < f.h; y++) for (let x = 0; x < f.w; x++) f.u[y * f.w + x] = c.u[(y >> 1) * c.w + (x >> 1)];
    relax(f, l === 0 ? 60 : 40);
  }

  // The solution is defined up to a constant: match the floor's median log luminance.
  const u = levels[0].u;
  const a: number[] = [];
  const b: number[] = [];
  for (let i = 0; i < n; i++) {
    if (floor[i]) {
      a.push(log[i]);
      b.push(u[i]);
    }
  }
  const offset = a.length ? median(Float32Array.from(a)) - median(Float32Array.from(b)) : 0;
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) out[i] = floor[i] ? Math.exp(u[i] + offset) : lum[i];
  return out;
}

/**
 * Retinex at half resolution (about 4x faster), upsampled bilinearly in log
 * space. Lighting is smooth, so the lost resolution does not matter; it is
 * blurred further afterwards anyway.
 */
function halfResRetinex(lum: Float32Array, floor: Uint8Array, w: number, h: number): Float32Array {
  const hw = Math.ceil(w / 2);
  const hh = Math.ceil(h / 2);
  const hl = new Float32Array(hw * hh);
  const hf = new Uint8Array(hw * hh);
  for (let y = 0; y < hh; y++) {
    for (let x = 0; x < hw; x++) {
      let sum = 0;
      let n = 0;
      let f = 0;
      for (let dy = 0; dy < 2; dy++) {
        for (let dx = 0; dx < 2; dx++) {
          const sx = Math.min(w - 1, x * 2 + dx);
          const sy = Math.min(h - 1, y * 2 + dy);
          sum += lum[sy * w + sx];
          f += floor[sy * w + sx];
          n++;
        }
      }
      hl[y * hw + x] = sum / n;
      hf[y * hw + x] = f >= 2 ? 1 : 0;
    }
  }
  const half = retinex(hl, hf, hw, hh);
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    const fy = Math.min(hh - 1, Math.max(0, (y - 0.5) / 2));
    const y0 = Math.floor(fy);
    const y1 = Math.min(hh - 1, y0 + 1);
    const ty = fy - y0;
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (!floor[i]) {
        out[i] = lum[i];
        continue;
      }
      const fx = Math.min(hw - 1, Math.max(0, (x - 0.5) / 2));
      const x0 = Math.floor(fx);
      const x1 = Math.min(hw - 1, x0 + 1);
      const tx = fx - x0;
      // Bilinear over floor cells only, so walls never bleed into the floor edge.
      let acc = 0;
      let wsum = 0;
      const add = (xx: number, yy: number, wt: number) => {
        const j = yy * hw + xx;
        if (!hf[j] || wt <= 0) return;
        acc += Math.log(Math.max(half[j], 1e-4)) * wt;
        wsum += wt;
      };
      add(x0, y0, (1 - tx) * (1 - ty));
      add(x1, y0, tx * (1 - ty));
      add(x0, y1, (1 - tx) * ty);
      add(x1, y1, tx * ty);
      out[i] = wsum > 0 ? Math.exp(acc / wsum) : lum[i];
    }
  }
  return out;
}

function median(values: Float32Array): number {
  if (!values.length) return 0.18;
  const sorted = values.slice().sort();
  return sorted[sorted.length >> 1];
}

/**
 * Pure part, unit-testable: `rgba` is the downsampled photo, `mask` is floor
 * coverage 0..1 at the same size.
 */
export function computeLighting(rgba: Uint8ClampedArray, w: number, h: number, mask: Float32Array): LightingMaps {
  const n = w * h;
  const lum = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    lum[i] = 0.2126 * SRGB_TO_LINEAR[rgba[o]] + 0.7152 * SRGB_TO_LINEAR[rgba[o + 1]] + 0.0722 * SRGB_TO_LINEAR[rgba[o + 2]];
  }

  // Lighting only: drop the old floor's pattern (tiles, paint, texture), then
  // any remaining soft-edged stains.
  const floor = new Uint8Array(n);
  for (let i = 0; i < n; i++) floor[i] = mask[i] > 0.5 ? 1 : 0;
  const illum = w > 256 ? halfResRetinex(lum, floor, w, h) : retinex(lum, floor, w, h);
  const clean = removeStains(illum, mask, w, h, Math.max(1, Math.round(w * 0.03)));

  // Normalized convolution: blur(lum * mask) / blur(mask).
  const radius = w * 0.015;
  const weighted = new Float32Array(n);
  const weights = mask.slice();
  const plain = clean.slice();
  for (let i = 0; i < n; i++) weighted[i] = clean[i] * mask[i];
  blur(weighted, w, h, radius);
  blur(weights, w, h, radius);
  blur(plain, w, h, radius);

  // Gloss follows the lighting too, so white tiles or paint don't read as reflections.
  const small = illum.slice();
  blur(small, w, h, Math.max(1, w * 0.002));

  const data = new Float32Array(n * 2);
  const floorVals: number[] = [];
  for (let i = 0; i < n; i++) {
    const wt = weights[i];
    // Far from any floor pixel the weighted value is noise; fade to the plain blur.
    const t = Math.min(1, wt / 0.05);
    const masked = wt > 1e-4 ? weighted[i] / wt : plain[i];
    const lb = masked * t + plain[i] * (1 - t);
    data[i * 2] = lb;
    data[i * 2 + 1] = small[i];
    if (mask[i] > 0.5) floorVals.push(lb);
  }
  const lref = Math.max(1e-3, median(Float32Array.from(floorVals.length ? floorVals : Array.from(plain))));
  return { width: w, height: h, data, lref };
}

/** Browser wrapper: downsamples the working photo and mask, then computes the maps. */
export function lightingFromCanvas(photo: HTMLCanvasElement, mask: Uint8Array, maskW: number, maskH: number): LightingMaps {
  const scale = Math.min(1, LIGHT_LONG_EDGE / Math.max(photo.width, photo.height));
  const w = Math.max(1, Math.round(photo.width * scale));
  const h = Math.max(1, Math.round(photo.height * scale));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(photo, 0, 0, w, h);
  const rgba = ctx.getImageData(0, 0, w, h).data;
  return computeLighting(rgba, w, h, downsampleMask(mask, maskW, maskH, w, h));
}

/** Area-average downsample of a 0..255 mask to 0..1 coverage. */
export function downsampleMask(mask: Uint8Array, mw: number, mh: number, w: number, h: number): Float32Array {
  const out = new Float32Array(w * h);
  const sx = mw / w;
  const sy = mh / h;
  for (let y = 0; y < h; y++) {
    const y0 = Math.floor(y * sy);
    const y1 = Math.max(y0 + 1, Math.floor((y + 1) * sy));
    for (let x = 0; x < w; x++) {
      const x0 = Math.floor(x * sx);
      const x1 = Math.max(x0 + 1, Math.floor((x + 1) * sx));
      let acc = 0;
      for (let yy = y0; yy < y1; yy++) for (let xx = x0; xx < x1; xx++) acc += mask[yy * mw + xx];
      out[y * w + x] = acc / (255 * (y1 - y0) * (x1 - x0));
    }
  }
  return out;
}
